import {
    Component, Input, ViewChild, ElementRef,
    AfterViewInit, OnChanges, OnDestroy, OnInit
} from '@angular/core';
import { SeriesData } from '@app/shared/interfaces/chartSeries.interface';
import { echarts, ECharts, EChartsCoreOption } from '../echarts.config';
import type { BarSeriesOption, LineSeriesOption } from 'echarts/charts';

@Component({
    selector: 'app-trendline-column',
    templateUrl: './trendline-column.component.html',
    styleUrl: './trendline-column.component.scss',
    standalone: false
})
export class TrendlineColumnComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    // Datos principales
    @Input() categories: string[] = [];
    @Input() seriesData: SeriesData[] = [];

    // Configuración
    @Input() title: string = '';
    @Input() subtitle: string = '';
    @Input() yAxisTitle: string = '';
    @Input() stackLabelsEnabled: boolean = true;
    @Input() trendlineData: number[] = [];
    @Input() trendlineName: string = 'Tendencia';

    @ViewChild('container', { static: true }) container!: ElementRef;

    private chart: ECharts | null = null;
    private themeObserver!: MutationObserver;

    ngOnInit(): void {
        const html = document.documentElement;
        const body = document.body;

        if (html.classList.contains('app-dark')) {
            body.classList.add('app-dark');
        } else {
            body.classList.remove('app-dark');
        }

        this.themeObserver = new MutationObserver(() => {
            if (html.classList.contains('app-dark')) {
                body.classList.add('app-dark');
            } else {
                body.classList.remove('app-dark');
            }

            this.chart?.dispose();
            this.initChart();
        });

        this.themeObserver.observe(html, {
            attributes: true,
            attributeFilter: ['class']
        });
    }

    ngAfterViewInit(): void {
        this.initChart();
    }

    ngOnChanges(): void {
        if (this.chart) {
            this.updateChart();
        }
    }

    ngOnDestroy(): void {
        this.themeObserver?.disconnect();
        this.chart?.dispose();
    }

    private isDark(): boolean {
        return document.body.classList.contains('app-dark');
    }

    private theme() {
        const dark = this.isDark();
        return {
            text: dark ? '#e5e7eb' : '#374151',
            muted: dark ? '#9ca3af' : '#6b7280',
            tooltipBg: dark ? '#1f2937' : '#ffffff',
            tooltipBorder: dark ? '#374151' : '#e5e7eb',
            axisLineColor: dark ? '#555' : '#ccc'
        };
    }

    private initChart(): void {
        this.container.nativeElement.innerHTML = '';

        const theme = this.isDark() ? 'dark' : undefined;
        this.chart = echarts.init(this.container.nativeElement, theme);
        this.chart.setOption(this.buildOption());

        setTimeout(() => this.chart?.resize(), 0);
    }

    private updateChart(): void {
        if (!this.chart) return;
        this.chart.setOption(this.buildOption(), { replaceMerge: ['series'] });
    }

    private buildOption(): EChartsCoreOption {
        const t = this.theme();

        return {
            backgroundColor: 'transparent',
            title: {
                text: this.title,
                subtext: this.subtitle,
                left: 'center',
                textStyle: { color: t.text },
                top: 0
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' }
            },
            legend: {
                top: 60,
                right: 0,
                orient: 'horizontal',
                textStyle: { color: t.text }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '13%',
                top: '25%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: this.categories,
                axisLine: { lineStyle: { color: t.axisLineColor } },
                axisLabel: { color: t.text }
            },
            yAxis: {
                type: 'value',
                name: this.yAxisTitle,
                nameLocation: 'middle',
                nameGap: 40,
                axisLine: { lineStyle: { color: t.axisLineColor } },
                axisLabel: { color: t.text },
                splitLine: { lineStyle: { color: t.axisLineColor, type: 'dashed' } }
            },
            series: this.getSeries()
        };
    }

    private getSeries(): (BarSeriesOption | LineSeriesOption)[] {
        const lastIndex = this.seriesData.length - 1;

        const barSeries: BarSeriesOption[] = this.seriesData.map((item, index) => ({
            name: item.name,
            type: 'bar',
            stack: 'total',
            data: item.values,
            ...(item.color ? { color: item.color } : {}),
            itemStyle: {
                borderRadius: index === lastIndex ? [4, 4, 0, 0] : [0, 0, 0, 0]
            },
            label: {
                show: true,
                position: 'inside',
                formatter: (params: any) => (params.value > 0 ? `${params.value}` : ''),
                fontSize: 12,
                color: '#fff'
            },
            emphasis: { focus: 'series' }
        }));

        if (!this.trendlineData?.length) return barSeries;

        const trendline: LineSeriesOption = {
            name: this.trendlineName,
            type: 'line',
            data: this.trendlineData,
            color: '#506276',
            lineStyle: {
                type: 'dashed',
                width: 2
            },
            symbol: 'circle',
            symbolSize: 8,
            itemStyle: { color: '#506276' },
            emphasis: { focus: 'series' },
            tooltip: {
                valueFormatter: (value) => `${value}`
            }
        };

        return [...barSeries, trendline];
    }
}